import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, User } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { onForegroundMessage } from '../firebase';

interface Notification {
  id: string;
  type: string;
  data: {
    comment?: { id: number; html: string };
    author?: { id: number; name: string; email: string };
    task?: { id: number; task_number: string; title: string };
  };
  read_at: string | null;
  created_at: string;
}

const NotificationBell: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const recentHandledRef = useRef<Record<string, number>>({});

  const shouldHandleOnce = useCallback((commentId?: string) => {
    if (!commentId) return true;
    const now = Date.now();
    const last = recentHandledRef.current[commentId];
    // Clean old entries
    Object.keys(recentHandledRef.current).forEach((k) => {
      if (now - (recentHandledRef.current[k] || 0) > 5000) delete recentHandledRef.current[k];
    });
    if (last && now - last < 2000) {
      // duplicate within 2s
      return false;
    }
    recentHandledRef.current[commentId] = now;
    return true;
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications');
      console.log('Notifications response:', response.data);
      const data = response.data as { data?: Notification[] };
      const notificationList = data.data || [];
      setNotifications(notificationList);
      const unread = notificationList.filter((n: Notification) => !n.read_at).length;
      console.log('Unread count:', unread, 'Total:', notificationList.length);
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const pollForNotification = useCallback(async (commentId: string, maxAttempts = 25) => {
    let attempts = 0;
    
    const poll = async (): Promise<boolean> => {
      attempts++;
      console.log(`🔍 Polling attempt ${attempts}/${maxAttempts}...`);
      
      try {
        const response = await api.get('/notifications');
        const data = response.data as { data?: Notification[] };
        const notificationList = data.data || [];
        
        // Check if any notification has this comment_id
        const foundNotification = notificationList.find(
          (n: Notification) => n.data?.comment?.id?.toString() === commentId
        );
        
        if (foundNotification) {
          console.log('✅ Notification found! Updating list...');
          setNotifications(notificationList);
          const unread = notificationList.filter((n: Notification) => !n.read_at).length;
          setUnreadCount(unread);
          return true;
        }
        
        if (attempts >= maxAttempts) {
          console.log('⏱️ Max polling attempts reached. Stopping...');
          return false;
        }
        
        // Wait 200ms before next attempt
        await new Promise(resolve => setTimeout(resolve, 200));
        return poll();
      } catch (error) {
        console.error('❌ Error polling notifications:', error);
        return false;
      }
    };
    
    await poll();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      console.log('⚠️ NotificationBell: Not authenticated, skipping listeners');
      return;
    }
    
    console.log('✅ NotificationBell: Setting up listeners');
    
    // Fetch notifications once on mount
    fetchNotifications();
    
    // Listen for messages from service worker
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      console.log('📨 Service worker message received:', event.data);
      
      // Check if this is a Firebase message with our custom data
      if (event.data && event.data.isFirebaseMessaging && event.data.data?.comment_id) {
        const commentId = event.data.data.comment_id as string;
        if (shouldHandleOnce(commentId)) {
          console.log(`⏳ SW → Polling for notification with comment_id: ${commentId}...`);
          setUnreadCount((c) => c + 1);
          pollForNotification(commentId);
        } else {
          console.log('🔁 SW message duplicate ignored for comment_id:', commentId);
        }
      } else if (event.data && event.data.type === 'NEW_NOTIFICATION') {
        // Fallback for custom message format
        const commentId = event.data.payload?.data?.comment_id;
        if (commentId) {
          if (shouldHandleOnce(commentId)) {
            console.log(`⏳ SW(fallback) → Polling for notification with comment_id: ${commentId}...`);
            setUnreadCount((c) => c + 1);
            pollForNotification(commentId);
          } else {
            console.log('🔁 SW fallback duplicate ignored for comment_id:', commentId);
          }
        } else {
          console.log('⚠️ No comment_id found, using fallback delay');
          // Do not optimistically increment without a concrete id; just refetch
          setTimeout(() => fetchNotifications(), 500);
        }
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    console.log('✅ Service worker message listener registered');
    
    // Also listen for foreground Firebase notifications
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('🔔 [App] Foreground FCM received:', payload);
      const commentId = payload?.data?.comment_id;
      
      if (commentId) {
        if (shouldHandleOnce(commentId)) {
          console.log(`⏳ Foreground → Polling for notification with comment_id: ${commentId}...`);
          setUnreadCount((c) => c + 1);
          pollForNotification(commentId);
        } else {
          console.log('🔁 Foreground duplicate ignored for comment_id:', commentId);
        }
      } else {
        console.log('⚠️ No comment_id in foreground notification, using fallback');
        // Do not optimistically increment without id; just refetch
        setTimeout(() => fetchNotifications(), 500);
      }
    });
    
    console.log('✅ Foreground FCM listener registered');
    
    return () => {
      console.log('🔌 Cleaning up listeners');
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [isAuthenticated, fetchNotifications, pollForNotification]);

  const markAsRead = async (notificationId: string) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    setIsOpen(false); // Close dropdown
    const taskId = notification.data.task?.id;
    if (taskId) {
      navigate(`/tasks/${taskId}`);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Don't render if not authenticated
  if (!isAuthenticated) return null;

  return (
    <>
      {/* Bell Icon */}
      <div className="relative">
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => {
            console.log('Current unreadCount:', unreadCount);
            setIsOpen(!isOpen);
          }}
          className="relative p-1.5 rounded-lg bg-white/50 hover:bg-white/80 transition-all duration-200 shadow-soft"
          title={`${unreadCount} unread notifications`}
        >
          <Bell className="w-4 h-4 text-gray-700" />
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </motion.button>
      </div>

      {/* Dropdown Portal - Rendered at body level */}
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/10 z-[9998]"
            onClick={() => setIsOpen(false)}
          />

          {/* Notification Panel - Fixed positioning */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 right-4 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-[9999] max-h-[600px] overflow-hidden flex flex-col"
          >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      disabled={loading}
                      className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Notifications List */}
              <div className="flex-1 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center">
                    <Bell className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-gray-500">No notifications yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {notifications.map((notification) => (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`p-4 cursor-pointer hover:bg-blue-50 transition-colors ${
                          !notification.read_at ? 'bg-blue-50/50' : ''
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              {notification.data.author?.name || 'Someone'} mentioned you
                            </p>
                            <p className="text-sm text-gray-600 mb-1">
                              in {notification.data.task?.task_number}: {notification.data.task?.title}
                            </p>
                            {notification.data.comment?.html && (
                              <div
                                className="text-xs text-gray-500 line-clamp-2"
                                dangerouslySetInnerHTML={{
                                  __html: notification.data.comment.html,
                                }}
                              />
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              {formatTimeAgo(notification.created_at)}
                            </p>
                          </div>
                          {!notification.read_at && (
                            <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-1" />
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
          </motion.div>
        </>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;
